import {
  AgenticLogicOnEmpty,
  AgenticLogicPlacementAssignmentDto,
  AgenticLogicPlacementDto,
  AgenticLogicRuleDto,
} from "@intelligems/ig-types";

/**
 * One grid line: a placement, plus the rule attached to it if there is one.
 * A placement with nothing attached still gets a row — it is the thing the
 * user just created and the thing they need in order to attach a rule.
 */
export interface PlacementRow {
  /** Row identity: the assignment when attached, else the placement itself. */
  id: string;
  placementId: string;
  placementName: string;
  /**
   * The placement's surface, or MISSING_REFERENCE_LABEL on a row rebuilt from
   * an assignment whose placement is gone — so it is a plain string, not the
   * surface union.
   */
  placementSurface: string;
  /**
   * The empty-result policy of the rule this row serves; null when nothing is
   * attached, or when the rules list no longer holds it. It is read off the
   * RULE rather than the placement because one rule answers this the same way
   * on every placement it fills.
   */
  ruleOnEmpty: AgenticLogicOnEmpty | null;
  /** Null when the placement has no rule attached yet. */
  assignment: AgenticLogicPlacementAssignmentDto | null;
  /** Null when nothing is attached; the missing label when the rule is gone. */
  ruleName: string | null;
  /** False for a row rebuilt from an assignment whose placement is gone. */
  placementExists: boolean;
  /** What this row serves: nothing yet, the whole org, or one experience. */
  attachment: RowAttachment;
}

/**
 * What a row is attached to — the answer the grid's "Attached to" column
 * renders.
 *
 * `global` is the placement's store-wide DEFAULT: serving resolves an
 * experience's own enabled row first and falls back to the global row for
 * every experience that has none (a disabled experience row falls back too).
 * It is still its own row rather than a nullable experience field, because a
 * merchant edits, pauses and deletes it independently of the experience rows
 * it backstops.
 */
export type RowAttachment =
  | { kind: "none" }
  | { kind: "global" }
  | { kind: "experience"; experienceName: string; variationName: string }
  | { kind: "unresolved-variation" };

/** Shown when a row points at something the org lists no longer hold. */
export const MISSING_REFERENCE_LABEL = "(missing)";

/** Names what the row serves, or null when nothing is attached to it. */
const attachmentOwnerLabel = (attachment: RowAttachment): string | null => {
  switch (attachment.kind) {
    case "none":
      return null;
    case "global":
      return "global";
    case "unresolved-variation":
      return "unresolved experiment variation";
    case "experience":
      return `${attachment.experienceName} — ${attachment.variationName}`;
  }
};

export const placementActionsLabel = (row: PlacementRow): string => {
  const owner = attachmentOwnerLabel(row.attachment);
  return `Actions for ${row.placementName}${owner ? `, ${owner}` : ""}`;
};

export type AssignmentsLoadState = "error" | "loading" | "empty" | "ready";

export const resolveAssignmentsLoadState = ({
  isError,
  isLoading,
  rowCount,
}: {
  isError: boolean;
  isLoading: boolean;
  rowCount: number;
}): AssignmentsLoadState => {
  if (isError) {
    return "error";
  }
  if (isLoading) {
    return "loading";
  }
  return rowCount === 0 ? "empty" : "ready";
};

/**
 * A variation-scoped row whose experience the server could not resolve still
 * reports its provenance rather than passing itself off as global. The DTO
 * carries `variationId` even when `experience` is null precisely so the two
 * cases stay distinguishable — `variationId` is not a foreign key, so an
 * assignment can outlive the experiment it was created for.
 */
export const describeAttachment = (
  assignment: AgenticLogicPlacementAssignmentDto | null
): RowAttachment => {
  if (!assignment) {
    return { kind: "none" };
  }
  if (!assignment.variationId) {
    return { kind: "global" };
  }
  if (!assignment.experience) {
    return { kind: "unresolved-variation" };
  }
  return {
    kind: "experience",
    experienceName: assignment.experience.name,
    variationName: assignment.experience.variationName,
  };
};

/**
 * Joins placements to their assignments and rules, placement-first. The three
 * lists are fetched independently and can disagree for a moment — a rule
 * deleted in another tab, a placement not yet in cache — so an unresolved
 * reference degrades to a labelled row rather than dropping the assignment,
 * which would hide the very row the user needs in order to clean it up.
 */
export const buildPlacementRows = (
  assignments: readonly AgenticLogicPlacementAssignmentDto[],
  placements: readonly AgenticLogicPlacementDto[],
  rules: readonly AgenticLogicRuleDto[]
): PlacementRow[] => {
  const rulesById = new Map(rules.map((rule) => [rule.id, rule]));

  const assignmentsByPlacement = new Map<
    string,
    AgenticLogicPlacementAssignmentDto[]
  >();
  for (const assignment of assignments) {
    const bucket = assignmentsByPlacement.get(assignment.placementId) ?? [];
    bucket.push(assignment);
    assignmentsByPlacement.set(assignment.placementId, bucket);
  }

  const attachedRow = (
    placement: Pick<
      PlacementRow,
      "placementId" | "placementName" | "placementSurface" | "placementExists"
    >,
    assignment: AgenticLogicPlacementAssignmentDto
  ): PlacementRow => {
    const rule = rulesById.get(assignment.ruleId);
    return {
      ...placement,
      id: assignment.id,
      assignment,
      ruleName: rule?.name ?? MISSING_REFERENCE_LABEL,
      ruleOnEmpty: rule?.settings.onEmpty ?? null,
      attachment: describeAttachment(assignment),
    };
  };

  const rows: PlacementRow[] = [];

  for (const placement of placements) {
    const base = {
      placementId: placement.id,
      placementName: placement.name,
      placementSurface: placement.surface,
      placementExists: true,
    };
    const attached = assignmentsByPlacement.get(placement.id) ?? [];
    if (attached.length === 0) {
      rows.push({
        ...base,
        id: placement.id,
        assignment: null,
        ruleName: null,
        ruleOnEmpty: null,
        attachment: { kind: "none" },
      });
      continue;
    }
    rows.push(...attached.map((assignment) => attachedRow(base, assignment)));
  }

  const knownPlacementIds = new Set(
    placements.map((placement) => placement.id)
  );
  for (const assignment of assignments) {
    if (knownPlacementIds.has(assignment.placementId)) {
      continue;
    }
    rows.push(
      attachedRow(
        {
          placementId: assignment.placementId,
          placementName: MISSING_REFERENCE_LABEL,
          placementSurface: MISSING_REFERENCE_LABEL,
          placementExists: false,
        },
        assignment
      )
    );
  }

  return rows;
};
